// StockOutSearch.jsx
import React, { useState, useEffect } from "react";
import { customFetch } from '../apiClient';
import StockOutEditModal from "./StockOutEditModal";

const StockOutSearch = () => {
  const [filters, setFilters] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    type: ""
  });

  const [stockOuts, setStockOuts] = useState([]);
  const [stockOutMeta, setStockOutMeta] = useState({ count: 0, totalItems: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expandedRows, setExpandedRows] = useState(new Set());

  // ✅ Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStockOutData, setEditStockOutData] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.type?.trim()) params.append("type", filters.type.trim());

      const res = await customFetch(`/StockOut/search?${params.toString()}`);

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      console.log("StockOut search:", data);

      const list = Array.isArray(data.data) ? data.data : [];

      setStockOuts(list);
      setStockOutMeta({
        count: data.count || list.length,
        totalItems: data.totalItems || 0
      });

      setSuccess(
        `Found ${data.count || list.length} stock out(s). Total Items: ${data.totalItems || 0}`
      );
      setCurrentPage(1);
      setExpandedRows(new Set());
    } catch (err) {
      setError(err.message);
      setStockOuts([]);
      setStockOutMeta({ count: 0, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Auto-search current date's data on page open
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setFilters({
      fromDate: new Date().toISOString().split("T")[0],
      toDate: new Date().toISOString().split("T")[0],
      type: ""
    });
    setStockOuts([]);
    setStockOutMeta({ count: 0, totalItems: 0 });
    setError("");
    setSuccess("");
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  // ✅ Edit handler
  const openEditModal = (stockOut) => {
    setEditStockOutData(stockOut);
    setShowEditModal(true);
  };

  // Delete handler with reversal
  const handleDelete = async (stockOut) => {
    if (
      !window.confirm(
        `Delete Stock Out: ${stockOut.recId}?\n\nType: ${stockOut.type}\nItems: ${stockOut.itemCount}\n\nThis will reverse ExpQTY/DmgQTY for all items.`
      )
    )
      return;

    setError("");
    setSuccess("");

    try {
      const res = await customFetch(
        `/StockOut/delete/${encodeURIComponent(stockOut.recId)}`,
        {
          method: "DELETE"
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to delete stock out");
      }

      const data = await res.json();
      console.log("Delete response:", data);

      setStockOuts((prev) => prev.filter((s) => s.recId !== stockOut.recId));

      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(stockOut.recId);
        return newSet;
      });

      setSuccess(
        `${data.message}: ${data.recId} (Type: ${data.type}, ${data.itemsDeleted} items, ${data.totalQtyReversed} qty reversed)`
      );
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
      console.error("Delete error:", err);
    }
  };

  const toggleExpand = (recId) => {
    setExpandedRows((prev) => {
      const s = new Set(prev);
      if (s.has(recId)) s.delete(recId);
      else s.add(recId);
      return s;
    });
  };

  // Pagination (same as before...)
  const effectiveRows = rowsPerPage || 10;
  const totalRecords = stockOuts.length;
  const totalPages = Math.ceil(totalRecords / effectiveRows);
  const startIndex = (currentPage - 1) * effectiveRows;
  const endIndex = startIndex + effectiveRows;
  const currentStockOuts = stockOuts.slice(startIndex, endIndex);

  const handleRowsPerPageChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      setRowsPerPage("");
      setCurrentPage(1);
      return;
    }
    let value = parseInt(rawValue);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const handleRowsPerPageBlur = (e) => {
    if (e.target.value === "" || !rowsPerPage) {
      setRowsPerPage(10);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (currentPage > 1) {
      pageNumbers.push(
        <button
          key="prev"
          className="page-btn"
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <i className="bi bi-chevron-left"></i>
        </button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`page-btn ${currentPage === i ? "active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages) {
      pageNumbers.push(
        <button
          key="next"
          className="page-btn"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      );
    }

    return pageNumbers;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const d = ("0" + date.getDate()).slice(-2);
    const m = ("0" + (date.getMonth() + 1)).slice(-2);
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const typeChipClass = (type) => {
    if (type === "EXPIRED") return "type-chip chip-expired";
    if (type === "DAMAGED") return "type-chip chip-damaged";
    return "type-chip chip-neutral";
  };

  return (
    <div className="sos-container">
      <div className="sos-card">
        {/* Header */}
        <div className="sos-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-search"></i></div>
            <div className="header-text">
              <h5>Stock Out Search</h5>
              <p>View, edit and reverse stock out entries</p>
            </div>
          </div>
          {stockOuts.length > 0 && (
            <div className="header-stats">
              <div className="hstat">
                <span className="hstat-val">{stockOutMeta.count}</span>
                <span className="hstat-lbl">Records</span>
              </div>
              <div className="hstat">
                <span className="hstat-val">{stockOutMeta.totalItems}</span>
                <span className="hstat-lbl">Items</span>
              </div>
            </div>
          )}
        </div>

        <div className="card-content">
          {/* Filters */}
          <div className="filter-bar">
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label"><i className="bi bi-calendar3"></i> From Date</label>
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label"><i className="bi bi-calendar3"></i> To Date</label>
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label"><i className="bi bi-funnel"></i> Type</label>
                <select
                  name="type"
                  value={filters.type}
                  onChange={handleFilterChange}
                  className="form-input"
                >
                  <option value="">All Types</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="DAMAGED">Damaged</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label"><i className="bi bi-list-ol"></i> Rows / page</label>
                <input
                  type="number"
                  className="form-input"
                  value={rowsPerPage}
                  placeholder="10"
                  min="1"
                  max="100"
                  onChange={handleRowsPerPageChange}
                  onBlur={handleRowsPerPageBlur}
                />
              </div>

              <div className="filter-actions">
                <button className="btn-search" onClick={handleSearch} disabled={loading}>
                  {loading
                    ? <><span className="spinner"></span> Searching...</>
                    : <><i className="bi bi-search"></i> Search</>}
                </button>
                <button className="btn-reset" onClick={handleReset}>
                  <i className="bi bi-arrow-clockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="alert-custom alert-danger">
              <i className="bi bi-exclamation-circle-fill"></i>
              <span>{error}</span>
              <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x-lg"></i></button>
            </div>
          )}
          {success && (
            <div className="alert-custom alert-success">
              <i className="bi bi-check-circle-fill"></i>
              <span>{success}</span>
              <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x-lg"></i></button>
            </div>
          )}

          {stockOuts.length > 0 && (
            <div className="result-info">
              Showing <strong>{startIndex + 1}</strong> to{" "}
              <strong>{Math.min(endIndex, totalRecords)}</strong> of{" "}
              <strong>{totalRecords}</strong> record(s)
            </div>
          )}

          {/* Results table */}
          <div className="table-wrap">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th style={{ width: "38px" }}></th>
                    <th className="col-sr">Sr</th>
                    <th>Rec ID</th>
                    <th>Rec No</th>
                    <th>RSRNO</th>
                    <th>Type</th>
                    <th>Created On</th>
                    <th>Created By</th>
                    <th className="num">Items</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="state-cell">
                        <div className="state-inner">
                          <span className="spinner spinner-dark"></span>
                          <span>Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : currentStockOuts.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="state-cell">
                        <div className="state-inner">
                          <i className="bi bi-inbox"></i>
                          <span>No stock out records found.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {currentStockOuts.map((stockOut, index) => (
                        <React.Fragment key={stockOut.recId}>
                          <tr className="parent-row">
                            <td className="text-center">
                              <button
                                className={`expand-btn ${expandedRows.has(stockOut.recId) ? "open" : ""}`}
                                onClick={() => toggleExpand(stockOut.recId)}
                                title="Toggle items"
                              >
                                <i className={`bi ${expandedRows.has(stockOut.recId) ? "bi-chevron-down" : "bi-chevron-right"}`} />
                              </button>
                            </td>
                            <td className="col-sr text-muted">{startIndex + index + 1}</td>
                            <td className="fw-700">{stockOut.recId}</td>
                            <td>{stockOut.recNo || "N/A"}</td>
                            <td>{stockOut.rsrno}</td>
                            <td>
                              <span className={typeChipClass(stockOut.type)}>{stockOut.type}</span>
                            </td>
                            <td>{formatDate(stockOut.createdOn)}</td>
                            <td>{stockOut.createdBy || "N/A"}</td>
                            <td className="num">
                              <span className="count-chip">
                                {stockOut.itemCount ?? stockOut.items?.length ?? 0}
                              </span>
                            </td>
                            <td className="col-actions">
                              <div className="btn-group-actions">
                                <button
                                  className="btn-icon btn-edit"
                                  title="Edit Stock Out"
                                  onClick={() => openEditModal(stockOut)}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                <button
                                  className="btn-icon btn-delete"
                                  title="Delete Stock Out"
                                  onClick={() => handleDelete(stockOut)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Nested item rows */}
                          {expandedRows.has(stockOut.recId) &&
                            stockOut.items?.map((item, itemIdx) => (
                              <tr key={itemIdx} className="child-row">
                                <td></td>
                                <td></td>
                                <td colSpan="2" className="child-med">
                                  <span className="tree-line">└─</span> {item.medicine}
                                </td>
                                <td className="child-sub">{item.category || "N/A"}</td>
                                <td className="child-sub">{item.brand || "N/A"}</td>
                                <td className="child-sub">
                                  <span className="mini-chip">Batch: {item.batchNo || "N/A"}</span>
                                </td>
                                <td className="child-sub">
                                  EXP: {item.exp ? formatDate(item.exp) : "N/A"}
                                </td>
                                <td className="child-sub">
                                  Leaf: {item.leaf || 0} | Piece: {item.piece || 0}
                                </td>
                                <td className="child-qty">
                                  Qty: {item.qty || 0} Pcs
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="pagination-bar">
              <small className="text-muted">
                Page {currentPage} of {totalPages}
              </small>
              <div className="pagination-btns">{renderPagination()}</div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Edit Modal */}
      <StockOutEditModal
        show={showEditModal}
        stockOutData={editStockOutData}
        onClose={() => {
          setShowEditModal(false);
          setEditStockOutData(null);
        }}
        onUpdate={handleSearch}
      />

      {/* STYLES */}
      <style jsx>{`
        .sos-container {
          --so-accent: #dc2626;
          --so-accent-dark: #b91c1c;
          --so-amber: #d97706;
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .sos-card {
          background: #fff;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 6px rgba(0,0,0,0.08);
          overflow: hidden;
        }

        /* Header */
        .sos-header {
          background: linear-gradient(135deg, var(--so-accent), var(--so-accent-dark));
          padding: 12px 16px;
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 12px;
          border-bottom: 3px solid #fca5a5;
        }
        .header-left { display: flex; align-items: center; gap: 12px; color: #fff; }
        .header-icon {
          width: 34px; height: 34px; border-radius: 8px;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center; font-size: 17px;
        }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; letter-spacing: .2px; }
        .header-text p { margin: 0; font-size: 11px; color: rgba(255,255,255,0.85); }
        .header-stats { display: flex; gap: 8px; }
        .hstat {
          background: rgba(255,255,255,0.92); border-radius: 8px;
          padding: 4px 14px; text-align: center; min-width: 64px;
          display: flex; flex-direction: column;
        }
        .hstat-val { font-size: 16px; font-weight: 800; color: var(--so-accent-dark); line-height: 1.1; }
        .hstat-lbl { font-size: 9px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: .4px; }

        .card-content { padding: 14px 16px; }

        /* Filter bar */
        .filter-bar {
          background: #f8fafc; border: 1px solid #e2e8f0;
          border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;
        }
        .filter-grid {
          display: grid; gap: 12px; align-items: end;
          grid-template-columns: repeat(4, minmax(120px, 180px)) 1fr;
        }
        .form-group { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .form-label {
          font-size: 11px; font-weight: 600; color: #475569;
          display: flex; align-items: center; gap: 5px; white-space: nowrap;
        }
        .form-label i { color: var(--so-accent); }
        .form-input {
          height: 34px; padding: 6px 9px;
          border: 1px solid #cbd5e1; border-radius: 7px;
          font-size: 12.5px; color: #1e293b; background: #fff;
          outline: none; transition: .15s; width: 100%;
        }
        .form-input:focus {
          border-color: var(--so-accent);
          box-shadow: 0 0 0 3px rgba(220,38,38,0.12);
        }
        select.form-input { cursor: pointer; }

        .filter-actions { display: flex; gap: 8px; }
        .btn-search, .btn-reset {
          height: 34px; padding: 0 18px; border: none; border-radius: 7px;
          font-size: 12px; font-weight: 700; cursor: pointer;
          display: inline-flex; align-items: center; gap: 6px; transition: .15s;
        }
        .btn-search { background: var(--so-accent); color: #fff; box-shadow: 0 2px 6px rgba(220,38,38,0.28); }
        .btn-search:hover:not(:disabled) { background: var(--so-accent-dark); }
        .btn-search:disabled { opacity: .6; cursor: not-allowed; box-shadow: none; }
        .btn-reset { background: #e2e8f0; color: #475569; }
        .btn-reset:hover { background: #cbd5e1; }

        /* Alerts */
        .alert-custom {
          margin-bottom: 12px; padding: 8px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 500;
          display: flex; align-items: center; gap: 8px;
          animation: fadeIn .2s ease;
        }
        .alert-custom span { flex: 1; }
        .alert-close {
          background: transparent; border: none; cursor: pointer;
          color: inherit; opacity: .55; font-size: 11px; display: flex;
        }
        .alert-close:hover { opacity: 1; }
        .alert-danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .alert-success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

        .result-info { font-size: 11.5px; color: #64748b; margin-bottom: 8px; }
        .result-info strong { color: #1e293b; }

        /* Table */
        .table-wrap { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
        .custom-table thead th {
          background: #f1f5f9; color: #475569;
          padding: 8px 10px; font-weight: 600; text-align: left;
          border-bottom: 1px solid #cbd5e1; white-space: nowrap;
          text-transform: uppercase; font-size: 10px; letter-spacing: .3px;
        }
        .custom-table thead th.num, .custom-table thead th.col-actions { text-align: center; }
        .custom-table td {
          padding: 7px 10px; border-bottom: 1px solid #eef2f7;
          color: #334155; vertical-align: middle;
        }
        .parent-row:hover { background: #fafbfc; }
        .num { text-align: center; }
        .text-center { text-align: center; }
        .text-muted { color: #94a3b8; }
        .fw-700 { font-weight: 700; color: #1e293b; }
        .col-sr { width: 46px; }
        .col-actions { width: 96px; }

        /* Expand button */
        .expand-btn {
          width: 24px; height: 24px; border: 1px solid #e2e8f0; border-radius: 6px;
          background: #f8fafc; color: #64748b; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 11px; transition: .15s;
        }
        .expand-btn:hover { border-color: var(--so-accent); color: var(--so-accent); }
        .expand-btn.open { background: var(--so-accent); border-color: var(--so-accent); color: #fff; }

        /* Chips */
        .type-chip {
          padding: 3px 12px; border-radius: 30px; font-weight: 700;
          font-size: 10px; letter-spacing: .4px; display: inline-block;
        }
        .chip-expired { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .chip-damaged { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
        .chip-neutral { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
        .count-chip {
          background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe;
          padding: 2px 10px; border-radius: 30px; font-weight: 700; font-size: 11px;
        }

        /* Action icons */
        .btn-group-actions { display: flex; justify-content: center; gap: 5px; }
        .btn-icon {
          width: 26px; height: 26px; border: none; border-radius: 5px;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 12px; transition: .15s;
        }
        .btn-edit { background: #f0fdf4; color: #16a34a; }
        .btn-edit:hover { background: #dcfce7; }
        .btn-delete { background: #fef2f2; color: #dc2626; }
        .btn-delete:hover { background: #fee2e2; }

        /* Child rows */
        .child-row { background: #f8fafc; }
        .child-row td { border-bottom: 1px solid #eef2f7; font-size: 11px; color: #475569; }
        .child-med { font-weight: 600; color: #1e293b; }
        .tree-line { color: #94a3b8; margin-right: 4px; }
        .child-sub { color: #64748b; }
        .child-qty { font-weight: 700; color: #16a34a; }
        .mini-chip {
          background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe;
          padding: 1px 7px; border-radius: 5px; font-size: 10px; font-weight: 600;
        }

        /* State cell (loading / empty) */
        .state-cell { padding: 28px 12px !important; }
        .state-inner {
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          color: #94a3b8;
        }
        .state-inner i { font-size: 26px; opacity: .6; }
        .state-inner span { font-size: 12px; font-style: italic; }

        /* Pagination */
        .pagination-bar {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 14px; flex-wrap: wrap; gap: 10px;
        }
        .pagination-btns { display: flex; gap: 5px; }
        .page-btn {
          min-width: 30px; height: 30px; padding: 0 9px;
          border: 1px solid #cbd5e1; border-radius: 6px; background: #fff;
          color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; transition: .15s;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .page-btn:hover { border-color: var(--so-accent); color: var(--so-accent); }
        .page-btn.active { background: var(--so-accent); border-color: var(--so-accent); color: #fff; }

        .spinner {
          width: 13px; height: 13px; border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          animation: spin .7s linear infinite;
        }
        .spinner-dark {
          border: 2px solid #cbd5e1; border-top-color: var(--so-accent);
          width: 18px; height: 18px;
        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Responsive */
        @media (max-width: 992px) {
          .filter-grid { grid-template-columns: repeat(2, 1fr); }
          .filter-actions { grid-column: span 2; }
        }
        @media (max-width: 560px) {
          .sos-header { flex-direction: column; align-items: stretch; }
          .header-stats { justify-content: center; }
          .filter-grid { grid-template-columns: 1fr; }
          .filter-actions { grid-column: auto; }
          .btn-search, .btn-reset { flex: 1; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default StockOutSearch;